import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import SubscriptionManager from '../services/dashboard/SubscriptionManager';

const initialState = {
  subscription: null,
  upcomingSubscription: null,
  scheduledSubscription: null,
  remainingDays: null,
  history: [],
  subscriptionById: null,
  status: 'idle',
  upcomingSubscriptionStatus: 'idle',
  error: null,
};

// Thunks
export const fetchCurrentSubscription = createAsyncThunk(
  'subscription/fetchCurrentSubscription',
  async (userId) => {
    const [history, remainingDays] = await Promise.all([
      SubscriptionManager.getUserSubscriptions(userId),
      SubscriptionManager.getRemainingDays(userId),
    ]);
    const subscription = history.find((s) => s.status === 'ACTIVE') || null;
    return { subscription, remainingDays, history };
  }
);

export const fetchUpcomingSubscription = createAsyncThunk(
  'subscription/fetchUpcomingSubscription',
  async (userId) => {
    const data = await SubscriptionManager.getUserSubscriptions(userId);
    const upcomingSubscription = data.find((s) => s.status === 'SCHEDULED') || null;
    return upcomingSubscription;
  }
);


export const subscribeUser = createAsyncThunk(
  'subscription/subscribeUser',
  async (subscriptionRequest) => {
    return await SubscriptionManager.subscribeUser(subscriptionRequest);
  }
);

export const renewSubscription = createAsyncThunk(
  'subscription/renewSubscription',
  async ({ userId, renewalRequest }) => {
    return await SubscriptionManager.renewSubscription(userId, renewalRequest);
  }
);

export const changeSubscriptionPlan = createAsyncThunk(
  'subscription/changeSubscriptionPlan',
  async ({ userId, changeRequest }) => {
    return await SubscriptionManager.changeSubscriptionPlan(userId, changeRequest);
  }
);

export const getSubscriptionById = createAsyncThunk(
  'subscription/getSubscriptionById',
  async (subscriptionId) => {
    return await SubscriptionManager.getSubscriptionById(subscriptionId);
  }
);

export const updateSubscriptionStatus = createAsyncThunk(
  'subscription/updateSubscriptionStatus',
  async ({ subscriptionId, newStatus }) => {
    return await SubscriptionManager.updateSubscriptionStatus(subscriptionId, newStatus);
  }
);

// Slice
const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    // ✅ Reset action to clear state on logout
    resetSubscription: (state) => {
      state.subscription = null;
      state.remainingDays = null;
      state.upcomingSubscription = null;
  state.scheduledSubscription = null;
      state.history = [];
      state.subscriptionById = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentSubscription.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCurrentSubscription.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.subscription = payload.subscription;
        state.remainingDays = payload.remainingDays;
        state.history = payload.history;
      })
      .addCase(fetchCurrentSubscription.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchUpcomingSubscription.pending, (state) => {
        state.upcomingSubscriptionStatus = 'loading';
      })
      .addCase(fetchUpcomingSubscription.fulfilled, (state, { payload }) => {
        state.upcomingSubscriptionStatus = 'succeeded';
        state.upcomingSubscription = payload;
      })
      .addCase(fetchUpcomingSubscription.rejected, (state, action) => {
        state.upcomingSubscriptionStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(subscribeUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(subscribeUser.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.subscription = payload;
      })
      .addCase(subscribeUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      .addCase(renewSubscription.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(renewSubscription.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.subscription = payload;
      })
      .addCase(renewSubscription.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      .addCase(changeSubscriptionPlan.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(changeSubscriptionPlan.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.subscription = payload;
      })
      .addCase(changeSubscriptionPlan.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      .addCase(getSubscriptionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getSubscriptionById.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.subscriptionById = payload;
      })
      .addCase(getSubscriptionById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      .addCase(updateSubscriptionStatus.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateSubscriptionStatus.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.subscription = payload;
      })
      .addCase(updateSubscriptionStatus.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

// ✅ Export resetSubscription
export const { resetSubscription} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
